import { useState } from "react";
import moment from "moment";
import useCalendarData from "../../../../hooks/useCalendarData";

// Utility to generate calendar grid for given month
const getCalendarGrid = (currentMonthStr) => {
  const startOfMonth = moment(currentMonthStr).startOf("month");
  const endOfMonth = moment(currentMonthStr).endOf("month");
  const startWeekday = startOfMonth.isoWeekday();
  const endWeekday = endOfMonth.isoWeekday();
  const daysBefore = startWeekday - 1;
  const daysAfter = 7 - endWeekday;

  const calendarStart = startOfMonth.clone().subtract(daysBefore, "days");
  const calendarEnd = endOfMonth.clone().add(daysAfter, "days");

  const grid = [];
  const currentDate = calendarStart.clone();

  while (currentDate.isSameOrBefore(calendarEnd, "day")) {
    grid.push({
      date: currentDate.toDate(),
      isCurrentMonth: currentDate.month() === startOfMonth.month(),
    });
    currentDate.add(1, "day");
  }

  return grid;
};

export default function CalendarWidget() {
  const [currentMonth] = useState(new Date());
  const { tableData, isLoading } = useCalendarData(currentMonth);

  const calendarGrid = getCalendarGrid(currentMonth);
  const today = new Date();

  return (
    <div className="bg-white p-5 rounded-xl border border-borderColor">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-gray-900">Calendar</h3>
        <div className="text-xs font-medium text-gray-500">
          {moment(currentMonth).format('MMMM YYYY')}
        </div>
      </div>

      {/* Legend */}
      <div className="flex justify-center gap-4 items-center mb-3 text-[11px]">
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block"></span>
          <span className="text-gray-500">Reminders</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-lime-600 inline-block"></span>
          <span className="text-gray-500">Events</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-rose-500 inline-block"></span>
          <span className="text-gray-500">Holidays</span>
        </span>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
          <span key={day} className="font-medium text-[11px] text-gray-400 py-2">{day}</span>
        ))}

        {isLoading ? (
          // Loading skeleton
          Array.from({ length: 35 }, (_, i) => (
            <div key={i} className="p-2 h-12 bg-gray-50 rounded-lg animate-pulse"></div>
          ))
        ) : (
          calendarGrid.slice(0, 35).map((cell, idx) => {
            const dateObj = cell.date;
            const dateStr = dateObj.toISOString().split("T")[0];
            const isToday = today.toDateString() === dateObj.toDateString();
            const isPast = dateObj < today && !isToday;

            const dayData = Array.isArray(tableData)
              ? tableData.find(
                  (d) => d?.date && new Date(d.date).toDateString() === dateObj.toDateString()
                )
              : null;

            const reminders = dayData?.reminders || [];
            const holidays = dayData?.holidays || [];
            const events = dayData?.events || [];
            const hasEvents = reminders.length > 0 || holidays.length > 0 || events.length > 0;

            return (
              <div
                key={dateStr + idx}
                className={`p-1 h-12 rounded-lg text-xs relative flex flex-col items-center justify-center cursor-pointer transition-colors
                  ${!cell.isCurrentMonth
                    ? "text-gray-300"
                    : isPast
                      ? "text-gray-400 hover:bg-gray-50"
                      : "text-gray-700 hover:bg-gray-50"
                  }
                  ${isToday ? "bg-primary-600 text-white hover:bg-primary-700 shadow-sm" : ""}
                `}
                title={hasEvents ? `${reminders.length} reminders, ${events.length} events, ${holidays.length} holidays` : ''}
              >
                <span className="font-medium">{dateObj.getDate()}</span>

                {/* Event indicators */}
                {hasEvents && (
                  <div className="flex gap-[2px] mt-0.5">
                    {reminders.length > 0 && (
                      <div className="w-1 h-1 rounded-full bg-indigo-500"></div>
                    )}
                    {events.length > 0 && (
                      <div className="w-1 h-1 rounded-full bg-lime-600"></div>
                    )}
                    {holidays.length > 0 && (
                      <div className="w-1 h-1 rounded-full bg-rose-500"></div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
